/**
 * The assistant's opening messages — the 06:05 morning briefing and the
 * "Now viewing as …" line a persona switch posts.
 *
 * Ports of two `addMsg('ai', …)` calls in
 * `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`:
 *
 * - demo line 4099 — the briefing seeded into the rail on load, stamped
 *   `06:05`, citing `fleet_overview`.
 * - demo line 4100 — the `#persona` change handler: "Now viewing as **X**
 *   (org). I'll weight answers to …" plus one button with that persona's
 *   first suggested question (`PSUGG[p.id][0]`).
 *
 * ## Computed, not canned
 *
 * fork(oem v1.8): Jay asked whether the assistant "is working on the ProjeX
 * platform, or are all the answers pre-built and generic". The demo's
 * briefing already computed its band list live; this port goes further.
 * Every fact in the briefing is read off the data at call time:
 *
 * - machine ids resolve through `IDS` (the data pack's alias map), so a pack
 *   that renumbers the fleet renumbers the briefing;
 * - sites come from each machine's `mine` in `FLEET`;
 * - alert ids, raise times and the live / shift-end mode come from `ALERTS`;
 * - incident report numbers come from `INCIDENTS`;
 * - the "N more machines" list is the condition register banded by
 *   `statusOf()`, worst first.
 *
 * What stays literal is the editorial half — which three items need a
 * decision and the few words describing each ({@link PICKS}). That is a
 * judgement a planner makes, not a figure the data holds.
 *
 * Engine layer (layer 2): pure. No React, no DOM, no clock, no randomness.
 *
 * @see ./local-answer.ts — the answer contract these messages reuse
 * @see docs/logic/workspace/chat-rail.md — "Chat chrome (v1.8)"
 */

import { BY_ID, FLEET, IDS } from '../../data/core.ts';
import type { ConditionBand, MineCode } from '../../schema/machine.ts';
import { CONDITION_BAND_LABEL } from '../../schema/machine.ts';
import type { OemPersonaId } from '../../schema/persona.ts';
import { MSHORT, statusOf } from '../oem-engine.ts';
import { ALERTS, type Alert } from '../seed/alerts.ts';
import { INCIDENTS, type Incident } from '../seed/incidents.ts';
import { PERSONA_SUGGESTIONS, suggestionText } from '../seed/suggestions.ts';
import type { OemAnswerAction } from './local-answer.ts';

/** An opening message — the parts of `OemAnswer` a seeded message needs. */
export interface OpeningMessage {
  readonly markdown: string;
  readonly sources: string[];
  readonly actions?: readonly OemAnswerAction[];
}

/**
 * Demo line 4099: the briefing is stamped `06:05` — five minutes after the
 * dataset's frozen "now" (`OEM_NOW_ISO`, 09 Sep 2026 06:00 AEST), as if the
 * overnight agents had just finished.
 */
export const BRIEFING_TIME = '06:05';

// ---------------------------------------------------------------------------
// The editorial picks
// ---------------------------------------------------------------------------

/**
 * What the briefing leads with. Aliases resolve through `IDS`; alert and
 * incident ids resolve through their seeds. Only the phrases are prose.
 */
const PICKS = {
  /** Three items that need a decision today. */
  decisions: [
    { alias: 'T22', what: 'brakes', note: 'empty-haul restriction drafted' },
    { alias: 'J07', what: 'rock drill', note: 'kit has to be air-freighted from Perth' },
    { alias: 'L14', what: 'reliability review', note: 'second early coupling — practice, not parts' },
  ],
  /** Closed incidents with a report and a replay. */
  closed: [
    { incident: 'IR-2026-014', what: 'parted drill string' },
    { incident: 'IR-2026-015', what: 'engine bay fire' },
  ],
  closedNote:
    'their fleet rules (breakout-torque trend, hydraulic top-ups, the contract PM-plan gap) are already live',
  /** The alert raised since the last shift that nobody has looked at yet. */
  fresh: {
    alert: 'AL-0925',
    what: 'charging connector',
    note: 'moisture after a wash-down, the only spare is at Burnie',
  },
} as const;

/** Worst band first — the demo's `BORDER`. */
const BAND_ORDER: Readonly<Record<ConditionBand, number>> = {
  major: 0,
  vpoor: 1,
  poor: 2,
  fair: 3,
  good: 4,
};

/** "Mt Isolde" → "Mount Isolde": the briefing reads as prose, not a card. */
const site = (mine: MineCode | string): string =>
  (MSHORT[mine as MineCode] ?? mine).replace(/^Mt /, 'Mount ');

const machineOf = (alias: string): string => IDS[alias] ?? alias;

/**
 * A machine or record id as the briefing prints it: the hyphen becomes a
 * non-breaking hyphen (U+2011) so a narrow rail never wraps "T-" from "09".
 */
const id = (value: string): string => value.replace(/-/g, '‑');

const mineOf = (machineId: string): string => BY_ID[machineId]?.mine ?? '';

const alertFor = (machineId: string): Alert | undefined =>
  ALERTS.find((a) => a.machine === machineId);

const incidentFor = (machineId: string): Incident | undefined =>
  INCIDENTS.find((i) => i.machine === machineId);

/** `'09 Sep 04:41'` → `'04:41'`. */
const raisedTime = (alert: Alert): string => alert.raised.split(' ').pop() ?? alert.raised;

/** One decision item, with whatever the seeds say about it. */
function decisionLine(pick: (typeof PICKS.decisions)[number]): string {
  const machine = machineOf(pick.alias);
  const where = site(mineOf(machine));
  const alert = alertFor(machine);
  const incident = alert ? undefined : incidentFor(machine);
  const facts: string[] = [];
  if (alert?.mode === 'LIVE') facts.push(`live alert ${raisedTime(alert)}`);
  facts.push(pick.note);
  const detail = facts.join(', ');
  // A review is about the machine, not a part of it: "the L-02 reliability review".
  if (incident) return `the **${id(machine)}** ${pick.what} at ${where} (${detail})`;
  return `**${id(machine)}**'s ${pick.what} at ${where} (${detail})`;
}

/** Machines in the two worst bands, excluding the ones already named. */
export function worstBandMachines(exclude: readonly string[]): { id: string; band: ConditionBand }[] {
  return FLEET.map((f) => ({ id: f.machine_id, band: statusOf(f.machine_id) }))
    .filter((m) => (m.band === 'major' || m.band === 'vpoor') && !exclude.includes(m.id))
    .sort((a, b) => BAND_ORDER[a.band] - BAND_ORDER[b.band]);
}

const COUNT_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five'];
const countWord = (n: number): string => COUNT_WORDS[n] ?? String(n);

/**
 * Demo line 4099. The morning briefing, computed from the live seeds.
 */
export function morningBriefing(): OpeningMessage {
  const decisions = PICKS.decisions.map(decisionLine);
  const decisionIds = PICKS.decisions.map((d) => machineOf(d.alias));

  const closed = PICKS.closed
    .map((c) => ({ pick: c, incident: INCIDENTS.find((i) => i.id === c.incident) }))
    .filter((c): c is { pick: (typeof PICKS.closed)[number]; incident: Incident } =>
      Boolean(c.incident?.status.startsWith('Closed')),
    )
    .map(
      ({ pick, incident }) =>
        `**${id(incident.machine)}**'s ${pick.what} at ${site(incident.mine)} (${id(incident.id)})`,
    );

  const fresh = ALERTS.find((a) => a.id === PICKS.fresh.alert);
  const freshLine = fresh
    ? ` New this morning: **${id(fresh.machine)}**'s ${PICKS.fresh.what} at ${site(mineOf(fresh.machine))} (${id(fresh.id)}) — ${PICKS.fresh.note}.`
    : '';

  const lead =
    `Morning. ${countWord(decisions.length)} things need a decision: ` +
    `${decisions.slice(0, -1).join(', ')}, and ${decisions[decisions.length - 1]}.`;

  const incidents = closed.length
    ? ` ${countWord(closed.length)} incidents this week are closed with reports and replays: ` +
      `${closed.join(' and ')}; ${PICKS.closedNote}.`
    : '';

  // "More" machines: the ones already named above are not listed twice.
  const worst = worstBandMachines(fresh ? [...decisionIds, fresh.machine] : decisionIds);
  const bands = worst
    .map((m) => `**${id(m.id)}** (${CONDITION_BAND_LABEL[m.band].toLowerCase()})`)
    .join(', ');
  const register = worst.length
    ? `The condition register also has **${worst.length}** more machines in the very poor or ` +
      `major-issue bands — ${bands} — each with an agent-drafted work order in Approvals.`
    : 'Nothing else in the condition register is in the very poor or major-issue bands.';

  const close =
    "Ask me about a trend, a machine, a cost, energy or a part — or tell me to write the weekly " +
    "status, the site risk email or the equipment deck and I'll build it from the live data. " +
    'I show which queries I ran; click a source chip to open the rows.';

  return {
    markdown: `${lead}${incidents}${freshLine}\n\n${register}\n\n${close}`,
    sources: ['fleet_overview'],
  };
}

// ---------------------------------------------------------------------------
// Persona switch — demo line 4100
// ---------------------------------------------------------------------------

/**
 * What each persona's answers are weighted to. The demo used
 * `p.desc.toLowerCase()` minus the full stop; five of the six read naturally
 * that way. The aftermarket persona's `desc` was rewritten in executive
 * language (fork P7a/A3) and reads awkwardly after "weight answers to", so
 * the phrase is carried here per persona.
 */
export const PERSONA_FOCUS: Readonly<Record<OemPersonaId, string>> = {
  rel: 'fleet-wide condition, trends, cost per hour and bulletins',
  rsm: 'approvals, customer commitments, warranty and technician deployment',
  tech: "today's jobs, procedures, isolation steps and the parts on the truck",
  parts: 'stock below minimum, requisitions, freight options and lead times',
  planner: 'the shift plan, PM due, agent drafts to approve and restrictions',
  aftermarket: 'cost to serve and where the fleet is drifting before it fails',
};

/** The persona's first suggested question, flattened against `IDS`. */
export function personaFirstQuestion(personaId: OemPersonaId): string | null {
  const first = PERSONA_SUGGESTIONS[personaId]?.[0];
  return first ? suggestionText(first, IDS) : null;
}

/**
 * Demo line 4100. Posted when the "VIEWING AS" persona changes.
 *
 * @param persona the persona now selected
 * @param org its org line with the client lockup substituted (`orgLabel()`)
 */
export function personaSwitchMessage(
  persona: { readonly id: OemPersonaId; readonly name: string },
  org: string,
): OpeningMessage {
  const q = personaFirstQuestion(persona.id);
  return {
    markdown: `Now viewing as **${persona.name}** (${org}). I'll weight answers to ${PERSONA_FOCUS[persona.id]}.`,
    sources: [],
    actions: q ? [{ label: q, prompt: q }] : undefined,
  };
}
